import { METRIC_DEFINITIONS, METRIC_KEYS } from './analytics.js';
const one = async (db, sql, params = []) => {
    const { rows } = await db.query(sql, params);
    return Number(rows[0]?.n ?? 0);
};
/**
 * Held in a Map rather than a plain object: lookup by metric key is then a
 * data structure access with no prototype chain behind it, so no key can ever
 * resolve to an inherited function.
 */
const QUERY_ENTRIES = {
    activation_rate: async (db) => ({
        numerator: await one(db, `SELECT count(DISTINCT i.organisation_id) AS n FROM onboarding_instances i
        WHERE i.went_live_at IS NOT NULL AND i.started_at > now() - interval '180 days'`),
        denominator: await one(db, `SELECT count(*) AS n FROM onboarding_instances
        WHERE started_at > now() - interval '180 days' AND status <> 'cancelled'`),
    }),
    time_to_value_days: async (db) => {
        const { rows } = await db.query(`SELECT percentile_cont(0.5) WITHIN GROUP
         (ORDER BY EXTRACT(EPOCH FROM (went_live_at - started_at)) / 86400) AS n
       FROM onboarding_instances
       WHERE went_live_at IS NOT NULL AND went_live_at > now() - interval '180 days'`);
        const raw = rows[0]?.n;
        const median = raw === null || raw === undefined ? null : Number(raw);
        return {
            numerator: median,
            denominator: 1,
            ...(median === null ? { incompleteReason: 'no go-lives in the trailing 180 days' } : {}),
        };
    },
    fleet_adoption_current_release: async (db) => {
        const latest = await db.query(`SELECT version FROM releases WHERE status = 'published'
        ORDER BY published_at DESC NULLS LAST LIMIT 1`);
        const version = latest.rows[0]?.version;
        if (!version) {
            return { numerator: null, denominator: null, incompleteReason: 'no published release' };
        }
        return {
            numerator: await one(db, `SELECT count(*) AS n FROM fleet_installations
          WHERE connectivity_state = 'connected' AND installed_version = $1`, [version]),
            denominator: await one(db, `SELECT count(*) AS n FROM fleet_installations WHERE connectivity_state = 'connected'`),
        };
    },
    retention_rate: async (db) => ({
        numerator: await one(db, `SELECT count(*) AS n FROM organisations o
        WHERE o.lifecycle_state = 'live' AND o.archived_at IS NULL
          AND EXISTS (SELECT 1 FROM organisation_lifecycle_events e
                       WHERE e.organisation_id = o.id AND e.to_state = 'live'
                         AND e.occurred_at < now() - interval '365 days')`),
        denominator: await one(db, `SELECT count(DISTINCT e.organisation_id) AS n FROM organisation_lifecycle_events e
        WHERE e.to_state = 'live' AND e.occurred_at < now() - interval '365 days'`),
    }),
    support_burden_per_org: async (db) => ({
        numerator: await one(db, `SELECT count(*) AS n FROM support_cases
        WHERE opened_at > now() - interval '90 days' AND severity <> 's4'`),
        denominator: await one(db, `SELECT count(*) AS n FROM organisations
        WHERE lifecycle_state = 'live' AND archived_at IS NULL`),
    }),
    sla_attainment: async (db) => ({
        numerator: await one(db, `SELECT count(*) AS n FROM support_cases
        WHERE opened_at > now() - interval '90 days'
          AND acknowledged_at IS NOT NULL AND acknowledged_at <= ack_due_at`),
        denominator: await one(db, `SELECT count(*) AS n FROM support_cases
        WHERE opened_at > now() - interval '90 days'
          AND (acknowledged_at IS NOT NULL OR now() > ack_due_at)`),
    }),
    expansion_signals: async (db) => ({
        numerator: await one(db, `SELECT count(*) AS n FROM drift_cases
        WHERE status <> 'resolved'
          AND drift_type IN ('usage_overage', 'capacity_over_provisioned', 'module_unexpected')`),
        denominator: 1,
    }),
    invoice_collection_rate: async (db) => ({
        numerator: await one(db, `SELECT count(*) AS n FROM b2b_invoices
        WHERE issue_date > now() - interval '180 days'
          AND status = 'paid' AND paid_at <= grace_until_date`),
        denominator: await one(db, `SELECT count(*) AS n FROM b2b_invoices
        WHERE issue_date > now() - interval '180 days'
          AND status NOT IN ('draft','void') AND due_date < now()`),
    }),
};
const QUERIES = new Map(Object.entries(QUERY_ENTRIES));
export async function computeMetric(db, key) {
    const def = METRIC_DEFINITIONS[key];
    const computedAt = new Date().toISOString();
    try {
        const query = QUERIES.get(key);
        if (!query)
            throw new Error(`no query defined for metric '${key}'`);
        const counts = await query(db);
        const { numerator, denominator } = counts;
        let value = null;
        let incomplete = false;
        let incompleteReason = counts.incompleteReason ?? null;
        if (numerator === null || denominator === null || incompleteReason) {
            incomplete = true;
            incompleteReason = incompleteReason ?? 'source table has no data';
        }
        else if (def.unit === 'percent') {
            if (denominator === 0) {
                incomplete = true;
                incompleteReason = 'denominator is zero — no qualifying records in the timeframe';
            }
            else {
                value = Math.round((numerator / denominator) * 1000) / 10;
            }
        }
        else if (def.unit === 'ratio') {
            if (denominator === 0) {
                incomplete = true;
                incompleteReason = 'denominator is zero — no qualifying records in the timeframe';
            }
            else {
                value = Math.round((numerator / denominator) * 100) / 100;
            }
        }
        else {
            value = Math.round(numerator * 10) / 10;
        }
        return {
            ...def,
            value,
            numeratorValue: numerator,
            denominatorValue: denominator,
            computedAt,
            incomplete,
            incompleteReason,
        };
    }
    catch (err) {
        return {
            ...def,
            value: null,
            numeratorValue: null,
            denominatorValue: null,
            computedAt,
            incomplete: true,
            incompleteReason: `computation failed: ${err.message.slice(0, 120)}`,
        };
    }
}
export async function computeAllMetrics(db) {
    return Promise.all(METRIC_KEYS.map((key) => computeMetric(db, key)));
}
//# sourceMappingURL=analytics-queries.js.map